import { useEffect, useState } from 'react';
import { KeyRound, Plus, Copy, CheckCircle, AlertCircle } from 'lucide-react';
import { api, formatDateTime } from '../../lib/api';

// Keys the Course Studio (on Mike's Mac) uses to publish Course Engine versions.
// A key is shown once, when it's created; the server keeps only a hash.
export default function AdminPublishKeys() {
  const [keys, setKeys] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const reload = () =>
    api('/api/admin/course-engine/publish-keys')
      .then((d) => setKeys(d.keys))
      .catch((e) => setError(e.message));

  useEffect(() => {
    reload();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const r = await api('/api/admin/course-engine/publish-keys', { method: 'POST', body: { name } });
      setCreated(r);
      setName('');
      reload();
    } catch (err) {
      setError(err.body?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (k) => {
    if (!confirm(`Revoke “${k.name}”? Anything using this key will stop being able to publish.`)) return;
    try {
      await api(`/api/admin/course-engine/publish-keys/${k.id}`, { method: 'DELETE' });
      if (created?.publish_key.id === k.id) setCreated(null);
      reload();
    } catch (err) {
      setError(err.message);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(created.key);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <h1 className="text-2xl font-bold mb-1 flex items-center gap-2">
        <KeyRound size={22} className="text-slate-400" /> Studio publishing
      </h1>
      <p className="text-slate-600 mb-6 max-w-2xl">
        The Course Studio publishes Course Engine versions to this site with a publish key. A key can list self-paced courses and publish to them, nothing else. Create one per Mac, paste it into the Studio’s Environments page, and revoke it if that Mac is lost or replaced.
      </p>

      {error ? (
        <p role="alert" className="mb-4 flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          <AlertCircle size={16} /> {error}
        </p>
      ) : null}

      <form onSubmit={create} className="mb-6 flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex-1 min-w-[14rem]">
          <label htmlFor="pk-name" className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">
            New key name
          </label>
          <input id="pk-name" className="input w-full" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Mike’s MacBook" maxLength={80} required />
        </div>
        <button type="submit" disabled={busy || !name.trim()} className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
          <Plus size={14} /> Create key
        </button>
      </form>

      {created ? (
        <div className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50 p-4" role="status">
          <p className="font-semibold text-emerald-900 flex items-center gap-1.5">
            <CheckCircle size={16} /> Key “{created.publish_key.name}” created. Copy it now: it won’t be shown again.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <code className="block flex-1 min-w-0 break-all rounded bg-white px-3 py-2 text-sm border border-emerald-200" aria-label="New publish key">
              {created.key}
            </code>
            <button type="button" onClick={copy} className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-2 text-sm font-semibold text-emerald-900">
              <Copy size={14} /> {copied ? 'Copied' : 'Copy key'}
            </button>
          </div>
        </div>
      ) : null}

      <div className="rounded-lg border border-slate-200 bg-white">
        {keys === null ? (
          <p className="p-4 text-sm text-slate-500">Loading…</p>
        ) : keys.length === 0 ? (
          <p className="p-4 text-sm text-slate-500">No publish keys yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Key</th>
                <th className="px-4 py-2">Created</th>
                <th className="px-4 py-2">Last used</th>
                <th className="px-4 py-2">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-medium">{k.name}</td>
                  <td className="px-4 py-2">
                    <code className="text-slate-600">{k.prefix}…</code>
                  </td>
                  <td className="px-4 py-2 text-slate-600">
                    {formatDateTime(k.created_at)}
                    {k.created_by_email ? <span className="block text-xs text-slate-500">{k.created_by_email}</span> : null}
                  </td>
                  <td className="px-4 py-2 text-slate-600">{k.last_used_at ? formatDateTime(k.last_used_at) : 'Never'}</td>
                  <td className="px-4 py-2 text-right">
                    {k.revoked_at ? (
                      <span className="text-xs font-semibold text-slate-500">Revoked {formatDateTime(k.revoked_at)}</span>
                    ) : (
                      <button type="button" onClick={() => revoke(k)} className="text-sm font-semibold text-rose-700 hover:underline">
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
